import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Copy, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { aiAPI } from "../../api";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

export default function WhatsAppConverter() {
  const [text, setText] = useState("");
  const [pro, setPro] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  async function convert() {
    if (!text.trim()) { setError("Paste a message first."); return; }
    setError(null);
    setLoading(true);
    setPro("");
    try {
      const data = await aiAPI.whatsapp(text);
      setPro(data.professional);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function copy() {
    if (!pro) return;
    navigator.clipboard?.writeText(pro);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/write" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Write
        </Link>
        <Chip tone="sky">WhatsApp → Professional</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Hinglish in. <em style={{ color: "var(--gold-500)" }}>Polished out.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Casual, broken, or mixed-language WhatsApp messages — converted to the English your manager or client expects.
        </p>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "stretch" }}>
        {/* Input */}
        <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          <p className="eyebrow">Your message</p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="bro tomorrow leave venum oru day, fever ah irukku"
            rows={6}
            style={{
              width: "100%", fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.55,
              border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
              padding: "10px 12px", resize: "vertical", background: "#fff", color: "var(--fg-1)", outline: "none",
              flex: 1,
            }}
          />
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{text.length} / 800</span>
        </div>

        {/* Convert button (desktop) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }} className="hidden md:flex">
          <Button variant="primary" onClick={convert} disabled={loading || !text.trim()} iconRight={<ArrowRight size={15} strokeWidth={1.8} />}>
            {loading ? "Converting…" : "Convert"}
          </Button>
        </div>

        {/* Output */}
        <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: pro ? "3px solid var(--sky)" : "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p className="eyebrow">Professional</p>
            {pro && (
              <button type="button" onClick={copy} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 10px", fontSize: 11, fontWeight: 600,
                background: copied ? "var(--sage)" : "#fff",
                color: copied ? "var(--paper)" : "var(--fg-2)",
                border: `1px solid ${copied ? "var(--sage)" : "var(--border-2)"}`,
                borderRadius: 999, cursor: "pointer",
              }}>
                {copied ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.6} />}
                {copied ? "Copied" : "Copy"}
              </button>
            )}
          </div>
          {loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--fg-3)", fontSize: 14 }}>
              <MessageCircle size={20} strokeWidth={1.4} style={{ opacity: 0.5, marginRight: 8 }} /> Converting…
            </div>
          ) : pro ? (
            <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--fg-1)", margin: 0, whiteSpace: "pre-wrap", flex: 1 }}>{pro}</p>
          ) : (
            <p style={{ fontSize: 14, color: "var(--fg-3)", margin: 0, fontStyle: "italic" }}>The polished version will appear here.</p>
          )}
        </div>
      </div>

      {/* Convert button (mobile) */}
      <div className="md:hidden">
        <Button variant="primary" onClick={convert} disabled={loading || !text.trim()} style={{ width: "100%", justifyContent: "center" }} iconRight={<ArrowRight size={15} strokeWidth={1.8} />}>
          {loading ? "Converting…" : "Convert"}
        </Button>
      </div>

      {error && <p style={{ color: "var(--berry)", fontSize: 13, margin: 0 }}>{error}</p>}
    </div>
  );
}
